/**
 * Program 12.1 UX4 — Bid bond operational tracker (tender.meta_json.bid_bond).
 */
export type BidBondStatus =
  | 'NOT_REQUIRED'
  | 'NOT_STARTED'
  | 'REQUESTED'
  | 'IN_PROGRESS'
  | 'RECEIVED'
  | 'VALIDATED'
  | 'EXPIRED';

export type BidBondState = {
  required: boolean;
  amount?: number;
  currency?: string;
  requestedAt?: string;
  expectedAt?: string;
  receivedAt?: string;
  expiryAt?: string;
  status: BidBondStatus;
};

export type BidBondReadiness = {
  status: BidBondStatus;
  ready: boolean;
  blocking: boolean;
  detail?: string;
};

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

export function parseBidBond(raw: unknown): BidBondState {
  if (!raw || typeof raw !== 'object') {
    return { required: false, status: 'NOT_REQUIRED' };
  }
  const o = raw as Record<string, unknown>;
  const required = o.required === true || asStr(o.status) !== 'NOT_REQUIRED';
  const status = (asStr(o.status) || (required ? 'NOT_STARTED' : 'NOT_REQUIRED')) as BidBondStatus;
  return {
    required,
    amount: o.amount != null ? Number(o.amount) : undefined,
    currency: asStr(o.currency) || 'AED',
    requestedAt: asStr(o.requested_at || o.requestedAt) || undefined,
    expectedAt: asStr(o.expected_at || o.expectedAt) || undefined,
    receivedAt: asStr(o.received_at || o.receivedAt) || undefined,
    expiryAt: asStr(o.expiry_at || o.expiryAt) || undefined,
    status,
  };
}

export function buildBidBondReadiness(raw: unknown): BidBondReadiness {
  const bond = parseBidBond(raw);
  if (!bond.required || bond.status === 'NOT_REQUIRED') {
    return { status: 'NOT_REQUIRED', ready: true, blocking: false };
  }
  const ready = bond.status === 'RECEIVED' || bond.status === 'VALIDATED';
  const blocking = bond.required && !ready && bond.status !== 'EXPIRED';
  return {
    status: bond.status,
    ready,
    blocking,
    detail: ready ? undefined : `Bid bond ${bond.status.replace(/_/g, ' ').toLowerCase()}`,
  };
}

export const BID_BOND_STATUS_OPTIONS: { value: BidBondStatus; label: string }[] = [
  { value: 'NOT_REQUIRED', label: 'Not required' },
  { value: 'NOT_STARTED', label: 'Not started' },
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'RECEIVED', label: 'Received' },
  { value: 'VALIDATED', label: 'Validated' },
  { value: 'EXPIRED', label: 'Expired' },
];
